/**
 * useAssistantChat — F13 VanaGreen AI chat state (PLAN.md §4.5, Faza F).
 *
 * Talks to the backend over Server-Sent Events via a raw `fetch` (not the
 * shared `http` client in `shared/lib/api.ts`, which buffers the whole
 * body before returning) — CSRF header/cookie handling mirrors that
 * client exactly so the same auth/CSRF middleware applies unchanged.
 */
import { useCallback, useRef, useState } from 'react';

import { BASE_URL, CSRF_COOKIE_NAME, getCookie, http } from '@/shared/lib/api';
import { posthog } from '@/shared/lib/posthog';

export interface AssistantConversation {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string | null;
  created_at: string;
}

export interface PendingToolConfirm {
  tool_use_id: string;
  tool_name: string;
  preview: string;
}

interface SseHandlers {
  onDelta: (text: string) => void;
  onToolConfirm: (data: PendingToolConfirm) => void;
  onDone: () => void;
  onError: (message: string) => void;
}

async function streamSse(path: string, body: unknown, handlers: SseHandlers): Promise<void> {
  const csrf = getCookie(CSRF_COOKIE_NAME);
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrf ? { 'x-csrf-token': csrf } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch {
    handlers.onError('Eroare de rețea');
    return;
  }

  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    let message = `[ERR_HTTP_${response.status}] Eroare la comunicarea cu asistentul`;
    try {
      const parsed = JSON.parse(text) as { error?: { code?: string; message?: string } };
      if (parsed.error) message = `[${parsed.error.code ?? 'ERR_UNKNOWN'}] ${parsed.error.message ?? ''}`;
    } catch {
      // not JSON — keep the generic message
    }
    handlers.onError(message);
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split('\n\n');
    buffer = chunks.pop() ?? '';
    for (const chunk of chunks) {
      const eventLine = chunk.split('\n').find((l) => l.startsWith('event: '));
      const dataLine = chunk.split('\n').find((l) => l.startsWith('data: '));
      if (!eventLine || !dataLine) continue;
      const event = eventLine.slice('event: '.length);
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(dataLine.slice('data: '.length)) as Record<string, unknown>;
      } catch {
        continue;
      }
      if (event === 'delta') handlers.onDelta(String(data.text ?? ''));
      else if (event === 'tool_confirm_required') {
        handlers.onToolConfirm({
          tool_use_id: String(data.tool_use_id),
          tool_name: String(data.tool_name),
          preview: String(data.preview ?? ''),
        });
      } else if (event === 'done') handlers.onDone();
      else if (event === 'error') handlers.onError(String(data.message ?? 'Eroare necunoscută'));
    }
  }
}

export function useAssistantChat(): {
  conversationId: string | null;
  messages: AssistantMessage[];
  streamingText: string;
  pendingTool: PendingToolConfirm | null;
  isBusy: boolean;
  error: string | null;
  conversations: AssistantConversation[];
  loadConversations: () => Promise<void>;
  startNewConversation: () => Promise<void>;
  openConversation: (id: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  confirmPendingTool: () => Promise<void>;
  dismissPendingTool: () => void;
} {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [pendingTool, setPendingTool] = useState<PendingToolConfirm | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<AssistantConversation[]>([]);
  const conversationIdRef = useRef<string | null>(null);

  const refreshMessages = useCallback(async (id: string) => {
    const res = await http.get<{ data: AssistantMessage[] }>(`/api/assistant/conversations/${id}/messages`);
    setMessages(res.data);
  }, []);

  const loadConversations = useCallback(async () => {
    const res = await http.get<{ data: AssistantConversation[] }>('/api/assistant/conversations');
    setConversations(res.data);
  }, []);

  const openConversation = useCallback(
    async (id: string) => {
      conversationIdRef.current = id;
      setConversationId(id);
      setPendingTool(null);
      setStreamingText('');
      setError(null);
      await refreshMessages(id);
    },
    [refreshMessages],
  );

  const startNewConversation = useCallback(async () => {
    const res = await http.post<{ data: AssistantConversation }>('/api/assistant/conversations', {});
    conversationIdRef.current = res.data.id;
    setConversationId(res.data.id);
    setMessages([]);
    setPendingTool(null);
    setStreamingText('');
    setError(null);
    setConversations((prev) => [res.data, ...prev]);
  }, []);

  const finishTurn = useCallback(
    async (id: string) => {
      setStreamingText('');
      await refreshMessages(id);
      setIsBusy(false);
    },
    [refreshMessages],
  );

  const sendMessage = useCallback(
    async (content: string) => {
      const id = conversationIdRef.current;
      if (!id || isBusy) return;
      setIsBusy(true);
      setError(null);
      setStreamingText('');
      setMessages((prev) => [
        ...prev,
        { id: `local-${Date.now()}`, role: 'user', content, created_at: new Date().toISOString() },
      ]);
      // Admin Monitoring product-analytics panel (PLAN.md 2026-09-07) — fact
      // only, zero message content/PII, same pattern as collection_created.
      posthog.capture('ai_assistant_message_sent');
      await streamSse(`/api/assistant/conversations/${id}/messages`, { content }, {
        onDelta: (text) => setStreamingText((prev) => prev + text),
        onToolConfirm: (data) => setPendingTool(data),
        onDone: () => void finishTurn(id),
        onError: (message) => {
          setError(message);
          setIsBusy(false);
          setStreamingText('');
        },
      });
    },
    [isBusy, finishTurn],
  );

  const confirmPendingTool = useCallback(async () => {
    const id = conversationIdRef.current;
    if (!id || !pendingTool || isBusy) return;
    const toolUseId = pendingTool.tool_use_id;
    setIsBusy(true);
    setError(null);
    setPendingTool(null);
    setStreamingText('');
    await streamSse(`/api/assistant/conversations/${id}/tool-confirm`, { tool_use_id: toolUseId }, {
      onDelta: (text) => setStreamingText((prev) => prev + text),
      onToolConfirm: (data) => setPendingTool(data),
      onDone: () => void finishTurn(id),
      onError: (message) => {
        setError(message);
        setIsBusy(false);
        setStreamingText('');
      },
    });
  }, [pendingTool, isBusy, finishTurn]);

  const dismissPendingTool = useCallback(() => setPendingTool(null), []);

  return {
    conversationId,
    messages,
    streamingText,
    pendingTool,
    isBusy,
    error,
    conversations,
    loadConversations,
    startNewConversation,
    openConversation,
    sendMessage,
    confirmPendingTool,
    dismissPendingTool,
  };
}
